'use client'

import { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'

import { stegaClean } from 'next-sanity'

import { ARTICLE_BLOCK_SPACING } from '@/app/utils/articleBlock'
import { loadScriptOnce } from '@/app/utils/loadScript'
import { cn } from '@/app/utils/style'
import type { TypeformEmbed as TypeformEmbedType } from '@/sanity.types'

const TYPEFORM_EMBED_SCRIPT = 'https://embed.typeform.com/next/embed.js'
const DEFAULT_HEIGHT = 50
// embed.js starts loading shortly before the form scrolls into view
const LOAD_AHEAD_MARGIN = '300px'

declare global {
  interface Window {
    tf?: { load?: () => void }
  }
}

interface TypeformEmbedProps {
  value: TypeformEmbedType
  className?: string
}

export function TypeformEmbed({ value, className }: TypeformEmbedProps) {
  const [containerRef, isNear] = useInView({ rootMargin: LOAD_AHEAD_MARGIN, triggerOnce: true })
  const formId = stegaClean(value.formId)
  const formUrl = stegaClean(value.formUrl)
  const label = stegaClean(value.label)
  const isCentered = stegaClean(value.alignment) === 'center'

  useEffect(() => {
    if (!isNear) return
    // embed.js scans the page when it loads; window.tf.load() covers blocks mounted after it.
    loadScriptOnce(TYPEFORM_EMBED_SCRIPT)
      .then(() => window.tf?.load?.())
      .catch(() => undefined)
  }, [isNear])

  return (
    <div
      className={cn(ARTICLE_BLOCK_SPACING, isCentered && 'text-center', className)}
      style={{ minHeight: value.height ?? DEFAULT_HEIGHT }}
    >
      <div ref={containerRef} data-tf-live={formId} suppressHydrationWarning>
        {formUrl && (
          <a href={formUrl} target="_blank" rel="noopener noreferrer" className="underline">
            {label || 'Open the form'}
          </a>
        )}
      </div>
    </div>
  )
}
